import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Footprints, MousePointerClick, Ticket, Star, Rocket, Radio } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ComingSoonBadge } from "@/business/components/ComingSoonBadge";
import { PageHeader } from "@/business/components/PageHeader";
import { COMING_SOON_STATS, COMING_SOON_CHART, COMING_SOON_PERFORMANCES, COMING_SOON_DEALS, COMING_SOON_REVIEWS } from "@/business/api";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card data-testid={testId} className="border-[#E5E7EB] shadow-sm relative overflow-hidden">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF1E8] text-[#FF6A00]"><Icon className="h-5 w-5" /></span>
            <div className="text-xs font-medium text-[#6B7280] mt-1">{label}</div>
          </div>
          <ComingSoonBadge testId={`${testId}-coming-soon-badge`} />
        </div>
        <div className="mt-3 text-2xl font-semibold tabular-nums text-[#111827]">{value}</div>
        <div className="text-[11px] text-[#6B7280] mt-1">Sample data — live analytics coming soon</div>
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const s = COMING_SOON_STATS;

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <PageHeader
        testId="dashboard-welcome-header"
        title={<span>Welcome back, <span className="text-[#FF6A00]">{user?.business_name || "venue owner"}</span>! 👋</span>}
        description="Here’s a preview of what your dashboard will look like. Live data lights up after you go live and start hosting gigs."
        action={(
          <Button onClick={() => navigate("/business/dashboard/hire")} className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="dashboard-overview-post-gig-button">
            Post a Gig
          </Button>
        )}
      />

      {/* Stats row */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Profile Views" value={s.profile_views.toLocaleString()} testId="dashboard-stat-profile-views" />
        <StatCard icon={Footprints} label="Foot Traffic (Check-ins)" value={s.foot_traffic.toLocaleString()} testId="dashboard-stat-foot-traffic" />
        <StatCard icon={MousePointerClick} label="Deal Clicks" value={s.deal_clicks.toLocaleString()} testId="dashboard-stat-deal-clicks" />
        <StatCard icon={Ticket} label="Deal Redemptions" value={s.deal_redemptions.toLocaleString()} testId="dashboard-stat-deal-redemptions" />
        <StatCard icon={Star} label="Average Rating" value={s.average_rating.toFixed(1)} testId="dashboard-stat-average-rating" />
      </section>

      {/* Chart + Upcoming */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 border-[#E5E7EB] shadow-sm" data-testid="dashboard-performance-card">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">Performance Overview</h2>
              <ComingSoonBadge />
            </div>
            <div className="text-xs text-[#6B7280]">Last 7 days</div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full" data-testid="dashboard-performance-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={COMING_SOON_CHART} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="views" name="Profile Views" stroke="#FF6A00" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="traffic" name="Foot Traffic" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="clicks" name="Deal Clicks" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB] shadow-sm" data-testid="dashboard-upcoming-performances-card">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">Upcoming Live Performances</h2>
              <ComingSoonBadge />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {COMING_SOON_PERFORMANCES.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] last:border-0 pb-3 last:pb-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="text-sm font-semibold truncate">{p.name}</span>{p.live && <Badge className="bg-[#FFF1E8] text-[#FF6A00] border border-[#FFD7BF] hover:bg-[#FFF1E8]"><Radio className="h-3 w-3 mr-1" />Live Now</Badge>}</div>
                  <div className="text-xs text-[#6B7280] truncate">{p.type} · {p.genre}</div>
                  <div className="text-xs text-[#6B7280]">{p.when}</div>
                </div>
                <Button variant="outline" size="sm" className="text-[#FF6A00] border-[#FFD7BF] hover:bg-[#FFF1E8]" disabled>Share</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {/* Top Deals + Reviews */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 border-[#E5E7EB] shadow-sm" data-testid="dashboard-top-deals-card">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div className="flex items-center gap-2"><h2 className="text-base font-semibold">Top Performing Deals</h2><ComingSoonBadge /></div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] text-[#6B7280] uppercase tracking-wider">
                    <th className="py-2 pr-2 font-medium">Deal</th>
                    <th className="py-2 px-2 font-medium">Status</th>
                    <th className="py-2 px-2 font-medium text-right">Clicks</th>
                    <th className="py-2 px-2 font-medium text-right">Redemptions</th>
                    <th className="py-2 pl-2 font-medium text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {COMING_SOON_DEALS.map((d, i) => (
                    <tr key={i} className="border-t border-[#E5E7EB]">
                      <td className="py-3 pr-2 font-medium">{d.name}</td>
                      <td className="py-3 px-2"><Badge className={d.status === "Active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border border-emerald-200" : "bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200"}>{d.status}</Badge></td>
                      <td className="py-3 px-2 text-right tabular-nums">{d.clicks ?? "—"}</td>
                      <td className="py-3 px-2 text-right tabular-nums">{d.redemptions ?? "—"}</td>
                      <td className="py-3 pl-2 text-right font-medium tabular-nums">{d.rate ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB] shadow-sm" data-testid="dashboard-recent-reviews-card">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div className="flex items-center gap-2"><h2 className="text-base font-semibold">Recent Reviews</h2><ComingSoonBadge /></div>
          </CardHeader>
          <CardContent className="space-y-4">
            {COMING_SOON_REVIEWS.map((r, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-[#FFF1E8] text-[#FF6A00] flex items-center justify-center text-sm font-semibold flex-shrink-0">{r.name.slice(0,1)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <span className="text-xs font-semibold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 tabular-nums">{r.rating.toFixed(1)}</span>
                  </div>
                  <div className="text-[11px] text-[#6B7280]">{r.when}</div>
                  <div className="text-sm text-[#374151] mt-1 line-clamp-2">{r.text}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {/* Upgrade banner */}
      <Card className="border-[#FFD7BF] bg-[#FFF8F1] shadow-sm" data-testid="dashboard-upgrade-banner">
        <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF6A00] text-white"><Rocket className="h-5 w-5" /></span>
            <div>
              <h3 className="text-base font-semibold">Reach more people and grow your business</h3>
              <p className="text-sm text-[#6B7280]">Boost your venue, run promotions, and get in front of thousands of music lovers.</p>
            </div>
          </div>
          <Button className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" disabled data-testid="dashboard-upgrade-button">Upgrade — Coming soon</Button>
        </CardContent>
      </Card>
    </div>
  );
}
